import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { AdvanceFeeTier } from '@varo/graphql-schema-types';
import {
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import HomeScreenBottomSheet from 'features/home/HomeScreen/components/HomeScreenBottomSheet/HomeScreenBottomSheet';
import useNotificationPreferences from 'features/myVaro/hooks/notifications/useNotificationPreferences';
import React, { useEffect, useRef, useState } from 'react';
import {
  RefreshControl,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated from 'react-native-reanimated';
import Button from 'shared/components/vxl/Button/Button';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import { SystemBanner } from 'shared/components/vxl/SystemBanner/SystemBanner';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import { SMALL_SCREEN_MAX_WIDTH } from 'shared/constants/layout';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import useAppQuickActions from 'shared/hooks/quickActions/useAppQuickActions';
import { ErrorDisplay } from 'shared/patterns/vxl/errors/ErrorDisplay/ErrorDisplay';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import RendererWithHorizontalPadding from 'shared/renderer/RendererWithHorizontalPadding';
import theme from 'shared/styles/theme';
import {
  enableBiometrics,
  hasPermanentRefreshToken,
  isUserRegistered,
} from 'shared/utils/biometricAuth/biometricAuth';

import useHomeScreenData from './hooks/useHomeScreenData/useHomeScreenData';
import useHomeTileViewportTracking from './hooks/useHomeTileViewportTracking/useHomeTileViewportTracking';

const HomeScreen = () => {
  const router = useRouter();
  const { isPinwheelLoading } = usePinwheel();
  const {
    encourageDdAfterVaSuccess,
    feeTier: rawFeeTier,
    refreshTileId,
  } = useLocalSearchParams<{
    encourageDdAfterVaSuccess?: string;
    feeTier?: AdvanceFeeTier;
    refreshTileId?: string;
  }>();

  const response = useHomeScreenData();
  const { segmentClient } = useAnalyticsConfiguration();

  const { fetching, refresh, content, error, homeScreenBottomSheetContent } =
    response;

  const { displayBanner, cards, navigationButton, appQuickActions } =
    content ?? {};

  useNotificationPreferences(() => {});

  const { width: screenWidth } = useWindowDimensions();

  useAppQuickActions(appQuickActions ?? []);

  const displayCards = cards ?? [];
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const hasAlertTileEventLoggedRef = useRef(false);

  const { onScroll, onScrollViewLayout, getLayoutHandler, onContainerLayout } =
    useHomeTileViewportTracking(displayCards);

  const [showBiometricEnableOption, setShowBiometricEnableOption] =
    useState(true);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'accountsHomeScreen',
  });

  useEffect(() => {
    if (!fetching && !isWeb && showBiometricEnableOption) {
      const enableBiometricsSupport = async () => {
        const isRegistered = await isUserRegistered();
        const isPermanentRefreshToken = await hasPermanentRefreshToken();
        if (!isRegistered && isPermanentRefreshToken) {
          await enableBiometrics();
        }
      };
      void enableBiometricsSupport();
    }

    if (fetching === 'refreshing') {
      setShowBiometricEnableOption(false);
    }
  }, [fetching, showBiometricEnableOption]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }
    const handleBackButton = (event: Event) => {
      event.preventDefault();
    };
    window.addEventListener('popstate', handleBackButton);
    window.history.pushState(null, '');
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);
  const currentUrl = usePathname();
  const isHomeScreenPresent = currentUrl === '/';
  const shouldEncourageDdAfterVaSuccess =
    encourageDdAfterVaSuccess === 'true' &&
    rawFeeTier === AdvanceFeeTier.HigherFee;
  const encourageDdBottomSheetContent = shouldEncourageDdAfterVaSuccess
    ? {
        onDismiss: () => {
          router.replace({
            pathname: '/',
            ...(refreshTileId
              ? {
                  params: {
                    refreshTileId,
                  },
                }
              : {}),
          });
        },
      }
    : null;

  const activeBottomSheetContent =
    encourageDdBottomSheetContent ?? homeScreenBottomSheetContent;

  const hasBottomSheet = !!activeBottomSheetContent;

  useEffect(() => {
    if (hasBottomSheet) {
      bottomSheetRef?.current?.present();
    }
  }, [hasBottomSheet]);

  // The encourage-DD request can arrive while another screen covers Home
  // (e.g. the instant transfer handoff), before the sheet is rendered, so the
  // effect above can't present it. When Home is already showing, both effects
  // call present() in the same commit; that's safe because a repeat present()
  // just snaps the open sheet to the same index.
  const shouldPresentEncourageDdBottomSheet =
    shouldEncourageDdAfterVaSuccess && isHomeScreenPresent;

  useEffect(() => {
    if (shouldPresentEncourageDdBottomSheet) {
      bottomSheetRef.current?.present();
    }
  }, [shouldPresentEncourageDdBottomSheet]);

  useEffect(() => {
    if (displayBanner && !hasAlertTileEventLoggedRef.current) {
      const { customAttributes } = displayBanner;

      void segmentClient.logEvent('Alert Tile Shown', {
        name: 'Alert Tile Shown',
        category: '',
        source_name: 'home_page',
        ...customAttributes,
      });

      hasAlertTileEventLoggedRef.current = true;
    }
  }, [displayBanner, segmentClient]);

  return (
    <>
      <StatusBar style="dark" />
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerShown: screenWidth <= SMALL_SCREEN_MAX_WIDTH || !isWeb,
            header: () => (
              <NavigationBar
                title=""
                primaryIcon={<VaroLogo width={59} height={21} />}
                primaryOnPress={() => {}}
                trailingAccessories={
                  navigationButton ? (
                    <Button
                      {...navigationButton}
                      onDark={false}
                      type="SECONDARY"
                    />
                  ) : null
                }
                color={theme.color.background.tertiary}
              />
            ),
          }}
        />
        <Animated.ScrollView
          showsVerticalScrollIndicator={!isWeb}
          contentContainerStyle={styles.contentContainer}
          scrollEventThrottle={100}
          onScroll={onScroll}
          onLayout={onScrollViewLayout}
          refreshControl={
            <RefreshControl
              refreshing={fetching === 'refreshing'}
              onRefresh={() => refresh(true)}
            />
          }
        >
          {displayBanner ? <SystemBanner {...displayBanner} /> : null}
          <View style={styles.cardsContainer} onLayout={onContainerLayout}>
            {displayCards.map((element) => {
              return (
                <View key={element.id} onLayout={getLayoutHandler(element.id)}>
                  <RendererWithHorizontalPadding
                    toRender={element}
                    paddingHorizontal="m"
                  />
                </View>
              );
            })}
          </View>
        </Animated.ScrollView>
        <ScreenTransitionOverlay
          isTabPage
          isLoading={fetching === 'loading' || isPinwheelLoading}
          LoadingComponent={
            <DirectDepositLoader
              isLoadingConditions={fetching === 'loading'}
              isPinwheelLoading={isPinwheelLoading}
            />
          }
          ErrorComponent={
            error ? (
              <ErrorDisplay
                error={error}
                displayType="tab"
                primaryButton={{ onPress: () => refresh(false) }}
                hasPageHeader={true}
              />
            ) : null
          }
        />
        {isHomeScreenPresent && activeBottomSheetContent ? (
          <HomeScreenBottomSheet
            bottomSheetRef={bottomSheetRef}
            data={activeBottomSheetContent}
          />
        ) : null}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  cardsContainer: {
    flex: 1,
    gap: theme.spacing.l,
    paddingVertical: theme.spacing.m,
  },
  container: { backgroundColor: theme.color.background.tertiary, flex: 1 },
  contentContainer: {
    backgroundColor: theme.color.background.tertiary,
    paddingBottom: theme.spacing.m,
  },
});

export default HomeScreen;
